/**
 * The showcase stage (Phase 6, BUILD_PLAN section 4): a dark cyclorama, a floor with a planar
 * reflection at Medium and above, a warm spot key with a shadow sized by the preset, two
 * rect-area fills with visible panels, and a cool rim. Every light exists from the first frame
 * and is only re-tuned afterwards, so a quality change never recompiles a lit pipeline. TSL node
 * materials only (CLAUDE.md).
 */
import { color, mix, normalView, positionViewDirection, reflector } from 'three/tsl';
import {
  BackSide,
  Mesh,
  MeshBasicNodeMaterial,
  MeshStandardNodeMaterial,
  Object3D,
  PlaneGeometry,
  RectAreaLight,
  RectAreaLightNode,
  SphereGeometry,
  SpotLight,
  type Node,
} from 'three/webgpu';
import { RectAreaLightTexturesLib } from 'three/addons/lights/RectAreaLightTexturesLib.js';

import type { EffectConfig } from '../../settings/presets.js';

/** The point the lights aim at (roughly a mech's chest). */
export const STAGE_FOCUS = { x: 0, y: 3, z: 0 } as const;

let ltcReady = false;

/** The LTC lookup tables rect-area lights need, initialised once per page. */
function ensureLtc(): void {
  if (ltcReady) return;
  RectAreaLightNode.setLTC(RectAreaLightTexturesLib.init());
  ltcReady = true;
}

/** A colour node is a vec3 at runtime; the typings only let colours scale by floats. */
function rgb(node: Node<'color'>): Node<'vec3'> {
  return node as unknown as Node<'vec3'>;
}

export interface StageQuality {
  /** Planar reflections on the floor (Medium and above; off in the e2e and at Low). */
  reflections: boolean;
  /** Shadow map size of the key light, or null for no shadow. */
  shadowMapSize: number | null;
}

export function stageQualityOf(config: EffectConfig, e2e: boolean): StageQuality {
  return {
    reflections: !e2e && config.preset !== 'low',
    shadowMapSize: config.shadows?.mapSize ?? null,
  };
}

export class Stage {
  readonly root = new Object3D();
  private readonly floor: Mesh;
  private readonly plainFloor: MeshStandardNodeMaterial;
  private readonly mirrorFloor: MeshStandardNodeMaterial;
  private readonly key: SpotLight;
  private readonly rim: SpotLight;
  private readonly fills: RectAreaLight[] = [];
  private readonly disposables: { dispose(): void }[] = [];
  private quality: StageQuality = { reflections: false, shadowMapSize: null };

  constructor(quality: StageQuality) {
    ensureLtc();
    const floorGeometry = new PlaneGeometry(70, 70);
    this.disposables.push(floorGeometry);

    const base = color(0x14171c);
    this.plainFloor = new MeshStandardNodeMaterial();
    this.plainFloor.colorNode = rgb(base);
    this.plainFloor.roughness = 0.55;
    this.plainFloor.metalness = 0.05;

    // The reflection renders the scene mirrored in the floor plane at half resolution; it only
    // runs while this material is the floor's, so Low and the e2e never pay for it.
    const reflection = reflector({ resolutionScale: 0.5 });
    reflection.target.rotateX(-Math.PI / 2);
    this.root.add(reflection.target);
    const grazing = normalView.dot(positionViewDirection).abs().oneMinus().pow(2);
    this.mirrorFloor = new MeshStandardNodeMaterial();
    this.mirrorFloor.colorNode = mix(rgb(base), reflection.rgb, grazing.mul(0.5).add(0.18));
    this.mirrorFloor.roughness = 0.35;
    this.mirrorFloor.metalness = 0.1;
    this.disposables.push(this.plainFloor, this.mirrorFloor, reflection);

    this.floor = new Mesh(floorGeometry, this.plainFloor);
    this.floor.rotateX(-Math.PI / 2);
    this.floor.receiveShadow = true;
    this.root.add(this.floor);

    const domeGeometry = new SphereGeometry(90, 32, 16);
    const dome = new MeshStandardNodeMaterial();
    dome.colorNode = rgb(color(0x0a0c10));
    dome.roughness = 1;
    dome.metalness = 0;
    dome.side = BackSide;
    this.disposables.push(domeGeometry, dome);
    this.root.add(new Mesh(domeGeometry, dome));

    const target = new Object3D();
    target.position.set(STAGE_FOCUS.x, STAGE_FOCUS.y, STAGE_FOCUS.z);
    this.root.add(target);

    this.key = new SpotLight(0xfff0dc, 1400, 90, 0.55, 0.45, 2);
    this.key.position.set(9, 16, 11);
    this.key.target = target;
    this.key.shadow.bias = -0.0002;
    this.key.shadow.normalBias = 0.05;
    this.key.shadow.camera.near = 1;
    this.key.shadow.camera.far = 70;
    this.root.add(this.key);

    this.rim = new SpotLight(0xa8d0ff, 900, 90, 0.7, 0.5, 2);
    this.rim.position.set(-8, 14, -14);
    this.rim.target = target;
    this.root.add(this.rim);

    for (const fill of [
      { hex: 0x9fc4ff, intensity: 5, width: 8, height: 12, position: [-13, 7, 5] as const },
      { hex: 0xffd6b0, intensity: 3, width: 6, height: 10, position: [12, 5, -8] as const },
    ]) {
      const light = new RectAreaLight(fill.hex, fill.intensity, fill.width, fill.height);
      light.position.set(fill.position[0], fill.position[1], fill.position[2]);
      light.lookAt(STAGE_FOCUS.x, STAGE_FOCUS.y, STAGE_FOCUS.z);
      // The visible emitter: a plane in the light's own XY, the rectangle it shines from.
      const panelGeometry = new PlaneGeometry(fill.width, fill.height);
      const panelMaterial = new MeshBasicNodeMaterial();
      panelMaterial.colorNode = rgb(color(fill.hex)).mul(1.3);
      this.disposables.push(panelGeometry, panelMaterial);
      const panel = new Mesh(panelGeometry, panelMaterial);
      panel.rotateY(Math.PI);
      light.add(panel);
      this.fills.push(light);
      this.root.add(light);
    }
    this.apply(quality);
  }

  /** Re-tunes the stage for a quality level without adding or removing lights. */
  apply(quality: StageQuality): void {
    this.quality = quality;
    this.floor.material = quality.reflections ? this.mirrorFloor : this.plainFloor;
    if (quality.shadowMapSize === null) {
      this.key.castShadow = false;
    } else {
      const shadow = this.key.shadow;
      if (shadow.mapSize.x !== quality.shadowMapSize) {
        shadow.mapSize.set(quality.shadowMapSize, quality.shadowMapSize);
        shadow.map?.dispose();
        shadow.map = null;
      }
      this.key.castShadow = true;
    }
  }

  current(): StageQuality {
    return this.quality;
  }

  dispose(): void {
    for (const d of this.disposables) d.dispose();
    this.key.dispose();
    this.rim.dispose();
    for (const fill of this.fills) fill.dispose();
    this.root.removeFromParent();
  }
}
